import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

const singletonKey = Symbol.for('peptidesskin.cart-sync');

function signature(cart) {
  return JSON.stringify([cart.item_count, cart.total_price, cart.items.map(item => [item.key, item.quantity, item.final_line_price])]);
}

function createCartSync() {
  let version = 0;
  let running;
  let dirty = false;
  let forceRender = false;
  let added = false;
  let mutationQueue = Promise.resolve();
  let renderedSignature;
  try { renderedSignature = JSON.stringify(JSON.parse(document.querySelector('[data-cart-signature]')?.textContent || 'null')); } catch { /* A refresh will recover invalid markup. */ }

  const announce = message => {
    const region = document.querySelector('[data-cart-sync-status]');
    if (region) region.textContent = message;
  };

  async function drain() {
    try {
      while (dirty) {
        const requestedVersion = version;
        await mutationQueue;
        if (requestedVersion !== version) continue;
        const cartUrl = new URL(`${Theme.routes.cart_url.replace(/\/$/, '')}.js`, window.location.origin);
        const response = await fetch(cartUrl, {cache: 'no-store', headers: {Accept: 'application/json'}});
        if (!response.ok) throw new Error('Cart read failed');
        const cart = await response.json();
        if (!Number.isInteger(cart.item_count) || cart.item_count < 0 || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
        if (requestedVersion !== version) continue;
        const currentSignature = signature(cart);
        if (forceRender || currentSignature !== renderedSignature) {
          const ids = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(item => item.dataset.sectionId))];
          if (ids.length) {
            const url = new URL(window.location.href);
            url.searchParams.delete('section_id');
            url.searchParams.set('sections', ids.join(','));
            const rendered = await fetch(url, {cache: 'no-store', headers: {Accept: 'application/json'}});
            if (!rendered.ok) throw new Error('Cart sections failed');
            const sections = await rendered.json();
            if (requestedVersion !== version) continue;
            for (const id of ids) {
              if (typeof sections[id] !== 'string' || !new DOMParser().parseFromString(sections[id], 'text/html').getElementById(`shopify-section-${id}`)) throw new Error('Missing cart section');
            }
            for (const id of ids) await morphSection(id, sections[id]);
          }
        }
        if (requestedVersion !== version) continue;
        const shouldOpen = added;
        renderedSignature = currentSignature;
        dirty = false;
        forceRender = false;
        added = false;
        for (const badge of document.querySelectorAll('[data-cart-count]')) {
          badge.textContent = String(cart.item_count);
          badge.hidden = cart.item_count === 0;
        }
        for (const trigger of document.querySelectorAll('[data-cart-trigger]')) trigger.setAttribute('aria-label', `${trigger.dataset.cartLabel}: ${cart.item_count}`);
        announce(`${document.querySelector('[data-cart-trigger]')?.dataset.cartLabel || 'Cart'}: ${cart.item_count}`);
        document.dispatchEvent(new CartUpdateEvent(cart, 'cart-sync', {source: 'cart-sync', itemCount: cart.item_count, added: shouldOpen}));
      }
      return true;
    } catch {
      // Never infer a new count or clear old HTML after a failed read/render.
      announce('Unable to refresh the cart. Please try again.');
      return false;
    } finally {
      running = undefined;
    }
  }

  function refresh({force = false, open = false} = {}) {
    version++;
    dirty = true;
    forceRender ||= force;
    added ||= open;
    if (!running) running = Promise.resolve().then(drain);
    return running;
  }

  document.addEventListener(ThemeEvents.cartUpdate, event => {
    if (event.detail?.data?.source === 'cart-sync') return;
    refresh({force: true, open: event instanceof CartAddEvent && !event.detail?.data?.didError});
  });
  document.addEventListener(ThemeEvents.cartError, () => refresh());
  document.addEventListener(ThemeEvents.discountUpdate, () => refresh({force: true}));
  window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });

  document.addEventListener('click', async event => {
    const trigger = event.target.closest?.('[data-cart-trigger]');
    if (!trigger || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    event.preventDefault();
    if (dirty && !(await (running || refresh()))) {
      window.location.assign(trigger.href);
      return;
    }
    await customElements.whenDefined('cart-drawer-component');
    drawer.open();
  });
  for (const [eventName, expanded] of [['dialog:open', 'true'], ['dialog:close', 'false']]) {
    document.addEventListener(eventName, event => {
      if (event.target.matches('cart-drawer-component')) for (const trigger of document.querySelectorAll('[data-cart-trigger][aria-expanded]')) trigger.setAttribute('aria-expanded', expanded);
    }, {capture: true});
  }

  return {
    refresh,
    queue(task) {
      const result = mutationQueue.then(task);
      mutationQueue = result.catch(() => {});
      return result;
    },
  };
}

const cartSync = window[singletonKey] ||= createCartSync();
export const refreshCart = options => cartSync.refresh(options);
export const queueCartMutation = task => cartSync.queue(task);
